import React, {useEffect} from 'react';
import {ActivityIndicator, StyleSheet, View} from 'react-native';
import {
  NavigationContainer,
  DefaultTheme,
  DarkTheme,
  type Theme,
} from '@react-navigation/native';
import {createNativeStackNavigator} from '@react-navigation/native-stack';
import {createBottomTabNavigator} from '@react-navigation/bottom-tabs';
import {useTranslation} from 'react-i18next';

import {ALL_FEATURES, useFleetFeatures, type FleetFeatures} from '@/entities/device/fleet';
import {useAuthStore} from '@/entities/session/auth-store';
import type {AppContext} from '@/entities/session/contexts';
import {documentTitle, linking} from '@/app/linking';
import {usePushMessages} from '@/app/push-messages';
import type {
  CamerasStackParamList,
  DevicesStackParamList,
  JournalStackParamList,
  OrgStackParamList,
  OverviewStackParamList,
  PeopleStackParamList,
  PersonnelStackParamList,
  ReportsStackParamList,
  RootStackParamList,
  SettingsStackParamList,
} from '@/app/routes';

import {LoginScreen} from '@/pages/auth/LoginScreen';
import {RegisterScreen} from '@/pages/auth/RegisterScreen';
import {VerifyEmailScreen} from '@/pages/auth/VerifyEmailScreen';
import {ForgotPasswordScreen} from '@/pages/auth/ForgotPasswordScreen';
import {ForcePasswordScreen} from '@/pages/auth/ForcePasswordScreen';
import {OnboardingScreen} from '@/pages/onboarding/OnboardingScreen';

import {OverviewScreen} from '@/pages/overview/OverviewScreen';
import {JournalScreen} from '@/pages/journal/JournalScreen';
import {PeopleScreen} from '@/pages/people/PeopleScreen';
import {PersonScreen} from '@/pages/people/PersonScreen';
import {PersonnelListScreen} from '@/pages/staff/PersonnelListScreen';
import {PersonnelCardScreen} from '@/pages/staff/PersonnelCardScreen';
import {PersonnelFormScreen} from '@/pages/staff/PersonnelFormScreen';
import {DetachedPersonnelScreen} from '@/pages/staff/DetachedPersonnelScreen';
import {ReportsScreen} from '@/pages/attendance/ReportsScreen';
import {DepartmentReportScreen} from '@/pages/attendance/DepartmentReportScreen';
import {EmployeeReportScreen} from '@/pages/attendance/EmployeeReportScreen';
import {PayrollScreen} from '@/pages/attendance/PayrollScreen';
import {DevicesScreen} from '@/pages/devices/DevicesScreen';
import {DeviceScreen} from '@/pages/devices/DeviceScreen';
import {DeviceSettingsScreen} from '@/pages/devices/DeviceSettingsScreen';
import {CameraSettingsScreen} from '@/pages/devices/CameraSettingsScreen';
import {CamerasScreen} from '@/pages/cameras/CamerasScreen';
import {ScanDeviceScreen} from '@/features/adopt-device/ui/ScanDeviceScreen';
import {ConfirmAdoptScreen} from '@/features/adopt-device/ui/ConfirmAdoptScreen';
import {MyAttendanceScreen} from '@/pages/employee/MyAttendanceScreen';
import {ScheduleScreen} from '@/pages/schedule/ScheduleScreen';
import {MyLogsScreen} from '@/pages/employee/MyLogsScreen';
import {SettingsScreen} from '@/pages/settings/SettingsScreen';
import {DepartmentsScreen} from '@/pages/settings/DepartmentsScreen';
import {DepartmentScreen} from '@/pages/settings/DepartmentScreen';
import {MembersScreen} from '@/pages/settings/MembersScreen';
import {TelegramRecipientsScreen} from '@/pages/settings/TelegramRecipientsScreen';
import {NotificationSettingsScreen} from '@/pages/settings/NotificationSettingsScreen';
import {CompanySettingsScreen} from '@/pages/settings/CompanySettingsScreen';
import {CalendarSettingsScreen} from '@/pages/settings/CalendarSettingsScreen';
import {TerminalProfilesScreen} from '@/pages/settings/TerminalProfilesScreen';
import {ApiBridgeScreen} from '@/pages/settings/ApiBridgeScreen';
import {AccountScreen} from '@/pages/settings/AccountScreen';
import {SecurityScreen} from '@/pages/settings/SecurityScreen';
import {PushSettingsScreen} from '@/pages/settings/PushSettingsScreen';

import {AppHeader} from '@/widgets/app-header/AppHeader';
import {AppSideBar} from '@/widgets/tab-bar/AppSideBar';
import {AppTabBar} from '@/widgets/tab-bar/AppTabBar';
import {useBreakpoint} from '@/shared/lib/breakpoints';
import {ErrorBoundary} from '@/shared/ui/error-boundary';
import {Icon, type IconName} from '@/shared/ui/icons';
import {Logo} from '@/shared/ui/logo';
import {spacing, useIsDark, useTheme} from '@/shared/ui/theme';

/**
 * Навигатор — проекция сессии, а не её владелец. Нигде нет вызова «перейти на
 * онбординг»: какой стек смонтирован, выводится из `status`, а какие вкладки — из
 * `activeContext.kind`. Экран меняет стор, стор меняет дерево. Именно поэтому
 * онбординг не залипает после создания компании, а экран входа не переживает
 * успешный вход.
 */
const Stack = createNativeStackNavigator<RootStackParamList>();
const Tab = createBottomTabNavigator();

const styles = StyleSheet.create({
  splash: {flex: 1, alignItems: 'center', justifyContent: 'center', gap: spacing(6)},
});

/** Общие настройки вложенных стеков: свой заголовок каждый экран рисует сам. */
const STACK = {headerShown: false} as const;

/**
 * Граница ошибок ВОКРУГ КАЖДОГО ЭКРАНА.
 *
 * Не одна на приложение, и это главное решение здесь. Большинство падений в рендере
 * случается в самом теле экрана (`report.data.summary`, когда данных ещё нет), а тело
 * экрана — снаружи его собственной `Page`, поэтому внутренняя граница страницы такие
 * ошибки не ловит: без этой обёртки они снимали бы ВЕСЬ кабинет вместе с панелью
 * вкладок. Здесь же падает один экран, а рама вокруг него — вкладки, боковая панель,
 * шапка — остаётся на месте, и уйти с него можно одним касанием.
 *
 * `route.key` как ключ сброса: новый экземпляр экрана — новая попытка.
 */
const screenLayout = ({
  children,
  route,
}: {
  children: React.ReactNode;
  route: {key: string};
}) => (
  <ErrorBoundary resetKey={route.key} testID="screen-error-boundary">
    {children}
  </ErrorBoundary>
);

/** «Главная» — пульт дня. Один экран: всё остальное отсюда уводит в свой раздел. */
const HomeNav = createNativeStackNavigator<OverviewStackParamList>();

function HomeStack() {
  return (
    <HomeNav.Navigator screenOptions={STACK} screenLayout={screenLayout}>
      <HomeNav.Screen name="Overview" component={OverviewScreen} />
    </HomeNav.Navigator>
  );
}

/**
 * «Журнал» — все проходы таблицей.
 *
 * Стек из одного экрана, и это не расточительство: у вкладки со стеком второе нажатие
 * по ней сбрасывает её к корню, а у голого экрана — нет. Журнал открывают из отчёта
 * суженным до одного человека, и «вернуть как было» одним нажатием по вкладке — ровно
 * то поведение, которого от неё ждут.
 */
const JournalNav = createNativeStackNavigator<JournalStackParamList>();

function JournalStack() {
  return (
    <JournalNav.Navigator screenOptions={STACK} screenLayout={screenLayout}>
      <JournalNav.Screen name="Journal" component={JournalScreen} />
    </JournalNav.Navigator>
  );
}

/** «Люди» личного пространства: список → карточка только на чтение. */
const PeopleNav = createNativeStackNavigator<PeopleStackParamList>();

function PeopleStack() {
  return (
    <PeopleNav.Navigator screenOptions={STACK} screenLayout={screenLayout}>
      <PeopleNav.Screen name="PeopleList" component={PeopleScreen} />
      <PeopleNav.Screen name="Person" component={PersonScreen} />
    </PeopleNav.Navigator>
  );
}

/** «Сотрудники»: список → карточка → форма. */
const PersonnelNav = createNativeStackNavigator<PersonnelStackParamList>();

function PersonnelStack() {
  return (
    <PersonnelNav.Navigator screenOptions={STACK} screenLayout={screenLayout}>
      <PersonnelNav.Screen name="PersonnelList" component={PersonnelListScreen} />
      <PersonnelNav.Screen name="PersonnelCard" component={PersonnelCardScreen} />
      <PersonnelNav.Screen name="PersonnelForm" component={PersonnelFormScreen} />
      <PersonnelNav.Screen name="PersonnelDetached" component={DetachedPersonnelScreen} />
    </PersonnelNav.Navigator>
  );
}

/** «Посещаемость»: компания → отдел → сотрудник, и ведомость за тот же период. */
const ReportsNav = createNativeStackNavigator<ReportsStackParamList>();

function ReportsStack() {
  return (
    <ReportsNav.Navigator screenOptions={STACK} screenLayout={screenLayout}>
      <ReportsNav.Screen name="ReportsOverview" component={ReportsScreen} />
      <ReportsNav.Screen name="DepartmentReport" component={DepartmentReportScreen} />
      <ReportsNav.Screen name="EmployeeReport" component={EmployeeReportScreen} />
      <ReportsNav.Screen name="Payroll" component={PayrollScreen} />
    </ReportsNav.Navigator>
  );
}

/** «Устройства»: терминалы и камеры → карточка → настройки, плюс подключение. */
const DevicesNav = createNativeStackNavigator<DevicesStackParamList>();

function DevicesStack() {
  return (
    <DevicesNav.Navigator screenOptions={STACK} screenLayout={screenLayout}>
      <DevicesNav.Screen name="DeviceList" component={DevicesScreen} />
      <DevicesNav.Screen name="Device" component={DeviceScreen} />
      <DevicesNav.Screen name="DeviceSettings" component={DeviceSettingsScreen} />
      <DevicesNav.Screen name="ScanDevice" component={ScanDeviceScreen} />
      <DevicesNav.Screen name="ConfirmAdopt" component={ConfirmAdoptScreen} />
    </DevicesNav.Navigator>
  );
}

/** «Камеры»: просмотр → настройки камеры или регистратора. */
const CamerasNav = createNativeStackNavigator<CamerasStackParamList>();

function CamerasStack() {
  return (
    <CamerasNav.Navigator screenOptions={STACK} screenLayout={screenLayout}>
      <CamerasNav.Screen name="CameraWall" component={CamerasScreen} />
      <CamerasNav.Screen name="CameraSettings" component={CameraSettingsScreen} />
    </CamerasNav.Navigator>
  );
}

/** «Отделы» своей вкладкой — у компании из одних камер. */
const OrgNav = createNativeStackNavigator<OrgStackParamList>();

function OrgStack() {
  return (
    <OrgNav.Navigator screenOptions={STACK} screenLayout={screenLayout}>
      <OrgNav.Screen name="Departments" component={DepartmentsScreen} />
      <OrgNav.Screen name="Department" component={DepartmentScreen} />
    </OrgNav.Navigator>
  );
}

/** «Настройки»: всё, что настраивают редко, разложенное по четырём группам. */
const SettingsNav = createNativeStackNavigator<SettingsStackParamList>();

function SettingsStack() {
  return (
    <SettingsNav.Navigator screenOptions={STACK} screenLayout={screenLayout}>
      <SettingsNav.Screen name="Settings" component={SettingsScreen} />
      <SettingsNav.Screen name="Departments" component={DepartmentsScreen} />
      <SettingsNav.Screen name="Department" component={DepartmentScreen} />
      <SettingsNav.Screen name="Members" component={MembersScreen} />
      <SettingsNav.Screen name="Telegram" component={TelegramRecipientsScreen} />
      <SettingsNav.Screen
        name="NotificationSettings"
        component={NotificationSettingsScreen}
      />
      <SettingsNav.Screen name="CompanySettings" component={CompanySettingsScreen} />
      <SettingsNav.Screen name="CalendarSettings" component={CalendarSettingsScreen} />
      <SettingsNav.Screen name="TerminalProfiles" component={TerminalProfilesScreen} />
      <SettingsNav.Screen name="ApiBridge" component={ApiBridgeScreen} />
      <SettingsNav.Screen name="Account" component={AccountScreen} />
      <SettingsNav.Screen name="Security" component={SecurityScreen} />
      <SettingsNav.Screen name="PushSettings" component={PushSettingsScreen} />
    </SettingsNav.Navigator>
  );
}

/** Тема навигатора, привязанная к палитре, чтобы переходы не мигали белым. */
function navTheme(dark: boolean, background: string, card: string, text: string): Theme {
  const base = dark ? DarkTheme : DefaultTheme;
  return {
    ...base,
    colors: {...base.colors, background, card, text, border: card, primary: text},
  };
}

type TabDef = {
  name: string;
  labelKey: string;
  /**
   * Короткая подпись для нижней панели телефона: «Явка», не «Посещаемость». В 78pt
   * вкладки длинное слово обрезалось до «Посещае…». Опущена — берётся `labelKey`.
   */
  shortKey?: string;
  icon: IconName;
  component: React.ComponentType<object>;
  /**
   * Возможность оборудования, без которой раздел пуст (`entities/device/fleet`).
   * Опущена — раздел нужен всегда.
   */
  needs?: keyof FleetFeatures;
  /** Раздел только для компании БЕЗ этой возможности — «Отделы» вместо всего учёта. */
  without?: keyof FleetFeatures;
};

/**
 * Набор вкладок — единственное, чем персоны отличаются друг от друга. Второе, что
 * его режет, — оборудование компании: разделы учёта без терминалов пусты (`needs`).
 *
 * ## Шесть разделов, пять вкладок
 *
 * На широком окне боковая панель показывает все шесть. Нижняя панель телефона
 * показывает не больше пяти (`AppTabBar`): в 390pt шестая вкладка обрезала подписи
 * до «Сотрудни…» и «Посещае…». Последние разделы списка — «Устройства» и
 * «Настройки», то, куда заходят раз в неделю и раз в месяц, — складываются в «Ещё».
 * Порядок здесь и есть приоритет: что стоит раньше, то на панели.
 *
 * Порядок один и тот же во всех персонах и читается как рабочий день: что сейчас →
 * кто прошёл → кто у нас есть → как ходили → чем это снято → как всё настроено.
 */
function tabsFor(kind: AppContext['kind'], features: FleetFeatures): TabDef[] {
  return allTabsFor(kind).filter(
    tab =>
      (!tab.needs || features[tab.needs]) && (!tab.without || !features[tab.without]),
  );
}

/**
 * Учёт рабочего времени — главная, журнал, люди, посещаемость, график — стоит на
 * терминалах и помечен `needs: 'attendance'`. У компании из одних камер остаются
 * «Отделы», «Устройства» и «Настройки» (владелец, 2026-10-22).
 */
function allTabsFor(kind: AppContext['kind']): TabDef[] {
  switch (kind) {
    case 'home':
      // Личное пространство держит «Посещаемость» по той же причине, что и компания:
      // люди и терминалы там настоящие, приезжают синхронизацией, и отчёт по явке
      // считается по тем же маршрутам с тем же companyId. Нет только «Сотрудников» —
      // заводить и править людей отсюда нечем.
      return [
        {name: 'Home', labelKey: 'nav.overview', icon: 'dashboard', component: HomeStack, needs: 'attendance'},
        {name: 'Journal', labelKey: 'nav.journal', icon: 'logs', component: JournalStack, needs: 'attendance'},
        {name: 'People', labelKey: 'nav.people', icon: 'people', component: PeopleStack, needs: 'attendance'},
        {name: 'Reports', labelKey: 'nav.attendance', shortKey: 'nav.tabAttendance', icon: 'attendance', component: ReportsStack, needs: 'attendance'},
        {name: 'Schedule', labelKey: 'nav.schedule', icon: 'calendar', component: ScheduleScreen, needs: 'attendance'},
        {name: 'Cameras', labelKey: 'nav.cameras', icon: 'camera', component: CamerasStack},
        {name: 'Devices', labelKey: 'nav.devices', icon: 'devices', component: DevicesStack},
        {name: 'Settings', labelKey: 'nav.settings', icon: 'settings', component: SettingsStack},
      ];
    case 'company':
      return [
        {name: 'Home', labelKey: 'nav.overview', icon: 'dashboard', component: HomeStack, needs: 'attendance'},
        {name: 'Journal', labelKey: 'nav.journal', icon: 'logs', component: JournalStack, needs: 'attendance'},
        {name: 'Personnel', labelKey: 'nav.staff', shortKey: 'nav.tabStaff', icon: 'personnel', component: PersonnelStack, needs: 'attendance'},
        {name: 'Reports', labelKey: 'nav.attendance', shortKey: 'nav.tabAttendance', icon: 'attendance', component: ReportsStack, needs: 'attendance'},
        {name: 'Schedule', labelKey: 'nav.schedule', icon: 'calendar', component: ScheduleScreen, needs: 'attendance'},
        {name: 'Org', labelKey: 'nav.departments', icon: 'building', component: OrgStack, without: 'attendance'},
        {name: 'Cameras', labelKey: 'nav.cameras', icon: 'camera', component: CamerasStack},
        {name: 'Devices', labelKey: 'nav.devices', icon: 'devices', component: DevicesStack},
        {name: 'Settings', labelKey: 'nav.settings', icon: 'settings', component: SettingsStack},
      ];
    case 'employee':
      return [
        {name: 'MyAttendance', labelKey: 'nav.myAttendance', icon: 'attendance', component: MyAttendanceScreen},
        {name: 'MyLogs', labelKey: 'nav.myLogs', icon: 'logs', component: MyLogsScreen},
        {name: 'Settings', labelKey: 'nav.settings', icon: 'settings', component: SettingsStack},
      ];
  }
}

/**
 * Вкладки активного контекста.
 *
 * `key={kind}` — чтобы смена персоны пересобрала набор с нуля: пять вкладок компании
 * не должны протекать в трёхвкладочного сотрудника.
 *
 * ## Где живёт имя экрана
 *
 * В теле страницы, и только там. Верхняя полоса приложения печатала имя ВКЛАДКИ, а
 * карточка под ней — имя ЭКРАНА, и на половине экранов эти две строки не совпадали:
 * над «Журналом событий» было написано «Ещё». Теперь на десктопе верхней полосы нет
 * вовсе (бренд, разделы, контекст и аккаунт держит боковая панель), а на телефоне
 * полоса тонкая и говорит только то, чего нет в теле: чья это компания и чей аккаунт.
 */
/**
 * «Камеры» и «Устройства» на телефоне не уходят в «Ещё» никогда (владелец,
 * 2026-10-22: «пусть они будут всегда видны»): в полном наборе вкладок их место
 * уступают последние из учёта.
 */
const PINNED_TABS = ['Cameras', 'Devices'] as const;

function MainTabs() {
  const {t} = useTranslation();
  const {isDesktop} = useBreakpoint();
  const activeContext = useAuthStore(s => s.activeContext);
  const kind = activeContext?.kind ?? 'home';
  // Сотруднику оборудование компании не видно, и его вкладки от него не зависят.
  const fleet = useFleetFeatures();
  const features = kind === 'employee' ? ALL_FEATURES : fleet.features;
  const tabs = tabsFor(kind, features);
  usePushMessages(tabs.map(tab => tab.name));
  const p = useTheme();

  // Пока не известно, что стоит на объекте, вкладок нет вовсе: полный набор, урезанный
  // через секунду до трёх, — это мигание, которое читается как сбой.
  if (kind !== 'employee' && !fleet.ready) {
    return (
      <View style={[styles.splash, {backgroundColor: p.background}]}>
        <ActivityIndicator size="small" color={p.primary} />
      </View>
    );
  }

  return (
    <Tab.Navigator
      // Смена набора — новый навигатор: вкладки учёта не должны протекать в кабинет
      // из одних камер, и наоборот.
      key={`${kind}:${features.attendance ? 'full' : 'video'}`}
      // Кабинет из одних камер открывается на камерах, а не на списке отделов.
      initialRouteName={features.attendance ? undefined : 'Cameras'}
      tabBar={props =>
        isDesktop ? <AppSideBar {...props} /> : <AppTabBar {...props} pinned={PINNED_TABS} />
      }
      screenOptions={{
        tabBarPosition: isDesktop ? 'left' : 'bottom',
        header: () => (isDesktop ? null : <AppHeader />),
        headerShown: !isDesktop,
      }}>
      {tabs.map(tab => (
        <Tab.Screen
          key={tab.name}
          name={tab.name}
          component={tab.component}
          options={{
            title: t(tab.labelKey),
            tabBarLabel: t(tab.shortKey ?? tab.labelKey),
            tabBarIcon: ({color, size}) => (
              <Icon name={tab.icon} color={color} size={size} />
            ),
          }}
        />
      ))}
    </Tab.Navigator>
  );
}

export function RootNavigator() {
  const p = useTheme();
  const dark = useIsDark();
  const status = useAuthStore(s => s.status);
  const hydrate = useAuthStore(s => s.hydrate);

  // Один раз на холодном старте читаем Keychain; стор разрешается в anonymous /
  // onboarding / ready, а дерево ниже просто следует за ним.
  useEffect(() => {
    void hydrate();
  }, [hydrate]);

  if (status === 'loading') {
    return (
      <View style={[styles.splash, {backgroundColor: p.background}]}>
        <Logo width={200} />
        <ActivityIndicator size="small" color={p.primary} />
      </View>
    );
  }

  return (
    <NavigationContainer
      theme={navTheme(dark, p.background, p.card, p.foreground)}
      linking={linking}
      documentTitle={documentTitle}>
      <Stack.Navigator screenOptions={{headerShown: false}} screenLayout={screenLayout}>
        {status === 'anonymous' ? (
          <>
            <Stack.Screen name="Login" component={LoginScreen} />
            <Stack.Screen name="Register" component={RegisterScreen} />
            <Stack.Screen name="VerifyEmail" component={VerifyEmailScreen} />
            <Stack.Screen name="Forgot" component={ForgotPasswordScreen} />
          </>
        ) : status === 'must-change-password' ? (
          <Stack.Screen name="ForcePassword" component={ForcePasswordScreen} />
        ) : status === 'onboarding' ? (
          <Stack.Screen name="Onboarding" component={OnboardingScreen} />
        ) : (
          <Stack.Screen name="Main" component={MainTabs} />
        )}
      </Stack.Navigator>
    </NavigationContainer>
  );
}
